// src/App.tsx
import React from "react";
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useLocation,
} from "react-router-dom";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/Login";
import { Provider } from "react-redux";
import { store, persistor } from "./app/store";
import { PersistGate } from "redux-persist/integration/react";
import { useAppSelector } from "./hooks/useAppSelector";
import AxiosClient from "./api/axiosClient";
import ResetPasswordPage from "./pages/ResetPassword";
import HealthCheck from "./pages/HealthCheck";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import ChangePasswordPage from "./pages/ChangePassword";

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{ element: JSX.Element }> = ({ element }) => {
  const { isAuthenticated, isPasswordResetRequired } = useAppSelector(
    (state) => state.auth,
  );
  const location = useLocation();

  if (isAuthenticated) {
    if (isPasswordResetRequired) {
      return <Navigate to="/login" />;
    }
    return element;
  }
  return <Navigate to="/login" state={{ from: location }} replace />;
};

const App: React.FC = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
  return (
    <>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <AxiosClient API_BASE_URL={API_BASE_URL}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <Routes>
                  <Route
                    path="/"
                    element={<ProtectedRoute element={<HomePage />} />}
                  />
                  <Route
                    path="/login"
                    element={<LoginPage redirectRoute="/" />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route
                    path="/change-password"
                    element={
                      <ProtectedRoute element={<ChangePasswordPage />} />
                    }
                  />
                  <Route path="/health" element={<HealthCheck />} />
                  <Route
                    path="/booking/confirmation/:bookingId"
                    element={
                      <ProtectedRoute element={<BookingConfirmationPage />} />
                    }
                  />
                </Routes>
              </BrowserRouter>
            </QueryClientProvider>
          </AxiosClient>
        </PersistGate>
      </Provider>
    </>
  );
};

export default App;
